import { EyeIcon, LogOutIcon, ServerIcon, UserIcon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import type { UserDto } from '@project-os/contracts';
import { useLogout } from '@/api/auth';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/common/user-avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function UserMenu({
  user,
  viewerMode,
}: {
  user: UserDto;
  /** Гостевой просмотр. Отсутствует вне проекта: смотреть там нечего. */
  viewerMode?: { active: boolean; toggle: () => void };
}) {
  const navigate = useNavigate();
  const logout = useLogout();

  const handleLogout = async () => {
    try {
      await logout.mutateAsync();
    } finally {
      // Даже при сетевой ошибке локальное состояние очищено — уводим на вход.
      void navigate('/login', { replace: true });
      toast.success('Вы вышли из системы');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 gap-2 px-1.5" aria-label="Меню пользователя">
          <UserAvatar user={user} size="md" />
          <span className="hidden max-w-32 truncate text-sm sm:inline">{user.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <div className="truncate text-sm font-medium">{user.name}</div>
          <div className="truncate text-xs text-muted-foreground">{user.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {viewerMode ? (
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              viewerMode.toggle();
            }}
            data-active={viewerMode.active}
            className="data-[active=true]:bg-accent"
          >
            <EyeIcon className="size-4" />
            {viewerMode.active ? 'Выйти из гостевого просмотра' : 'Показать, как видит Viewer'}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={() => void navigate('/profile')}>
          <UserIcon className="size-4" />
          Мой профиль
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void navigate('/system')}>
          <ServerIcon className="size-4" />
          Состояние системы
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          disabled={logout.isPending}
          onSelect={(event) => {
            event.preventDefault();
            void handleLogout();
          }}
        >
          <LogOutIcon className="size-4" />
          Выйти
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
